import {Category, DayAvailability} from "@/data/entities/menu";
import {DAYS, Day, DayAvailabilityEditValues} from "@/data/entities/menuEditor";

/**
 * - `closed`: not available on this day at all
 * - `times`: only from and/or until a time
 */
export type DayPlanningMode = "closed" | "times";

export type DayPlanning = {
    day: Day,
    custom: boolean, // `false`: available all day (no day availability)
    mode: DayPlanningMode,
    timeFrom: string, // `HH:mm` (time input), empty for start of the day
    timeUntil: string, // `HH:mm` (time input), empty for end of the day
};

export type CategoryPlanningFormValues = {
    days: DayPlanning[], // Monday to Sunday
};

/** `1600` -> `16:00` */
const toInputTime = (time: string | null): string => time ? `${time.slice(0, 2)}:${time.slice(2, 4)}` : "";

/** `16:00` -> `1600`, empty -> `null` */
const toApiTime = (time: string): string | null => time === "" ? null : time.replace(":", "").slice(0, 4);

export const toCategoryPlanningFormValues = (category: Category): CategoryPlanningFormValues => ({
    days: DAYS.map((day: Day): DayPlanning => {
        const availability: DayAvailability | undefined = category.availability.find(a => a.day === day);
        const disabled: boolean = availability?.disabled === true;
        const timeFrom: string = toInputTime(availability?.timeFrom ?? null);
        const timeUntil: string = toInputTime(availability?.timeUntil ?? null);

        return {
            day,
            custom: disabled || timeFrom !== "" || timeUntil !== "",
            mode: disabled ? "closed" : "times",
            timeFrom,
            timeUntil,
        };
    }),
});

/**
 * Only days with a custom planning get a day availability; other days are
 * available all day.
 */
export const toDayAvailabilities = (form: CategoryPlanningFormValues): DayAvailabilityEditValues[] => (
    form.days
        .filter((day: DayPlanning) => day.custom)
        .map((day: DayPlanning): DayAvailabilityEditValues => day.mode === "closed"
            ? {day: day.day, disabled: true, timeFrom: null, timeUntil: null}
            : {day: day.day, disabled: false, timeFrom: toApiTime(day.timeFrom), timeUntil: toApiTime(day.timeUntil)}
        )
);